import { useNavigate, useParams } from "../lib/router-context";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOpenLobbyStore } from "../lib/games/openlobby/store";
import { useGameSocket } from "../lib/useGameSocket";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { trackEvent } from "../lib/analytics";
import { ThemeToggle } from "../components/ThemeToggle";
import { HomeLink } from "../components/HomeLink";
import { Button } from "../components/ui/Button";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { MobileNav } from "../components/navbar/NavbarMobile";
import { NavButtons } from "../components/navbar/NavbarButtons";
import { SectionHeader } from "../components/SectionHeader";
import { TabButton, TabGroup } from "../components/ui/Tabs";
import { Timeline } from "../components/Timeline";
import { EmptyState } from "../components/EmptyState";
import { OpenLobbyCard } from "../components/OpenLobbyCard";
import { ProfileAvatar } from "../components/ProfileAvatar";
import { ProviderBadge } from "../components/ProviderBadge";
import { GeoBingoCard } from "../components/GeoBingoCard";
import { SvgIcon } from "../components/ui/SvgIcon";
import mapIcon from "../assets/map-icon.svg?raw";
import { playNewRoundSound } from "../lib/sounds";

export function OpenLobbyScorePage() {
  const { lobbyId } = useParams() as { lobbyId: string };
  const navigate = useNavigate();
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  useGameSocket(lobbyId, useOpenLobbyStore);

  const gameState = useOpenLobbyStore((state) => state.gameState);
  const lobbyState = useOpenLobbyStore((state) => state.lobbyState);
  const currentPlayerId = useAppStore((state) => state.id);

  const [gameRemaining, setGameRemaining] = useState<number | null>(null);
  const [scoreCountdown, setScoreCountdown] = useState<number | null>(null);
  const prevPhase = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!prevPhase.current) {
      prevPhase.current = gameState?.phase;
      return;
    }
    if (prevPhase.current === "Score" && gameState?.phase === "Game") {
      playNewRoundSound();
    }
    prevPhase.current = gameState?.phase;
  }, [gameState?.phase]);

  useEffect(() => {
    if (!gameState?.start_time) return;
    const startMs = new Date(gameState.start_time).getTime();
    const gameEnd = startMs + gameState.game_duration * 1000;
    const roundEnd = gameEnd + gameState.score_duration * 1000;

    const tick = () => {
      const now = Date.now();
      setGameRemaining(Math.max(0, Math.round((gameEnd - now) / 1000)));
      setScoreCountdown(Math.max(0, Math.round((roundEnd - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    gameState?.start_time,
    gameState?.game_duration,
    gameState?.score_duration,
  ]);

  const navbarButtonsEndscreen = [
    NavButtons.scoreboard,
    NavButtons.playerPaths,
  ] as const;
  const [mobileTabEndscreen, setMobileTabEndscreen] = useState<
    (typeof navbarButtonsEndscreen)[number]
  >(NavButtons.scoreboard);
  const [sortBy, setSortBy] = useState<"moves" | "time">("moves");

  useEffect(() => {
    if (!gameState) return;
    trackEvent("openlobby-sort-change", { lobbyId, sortBy });
  }, [sortBy]);

  if (!gameState) {
    return <GameLoadingScreen />;
  }

  const hasPlayedActiveRound =
    (gameState.players[currentPlayerId]?.past_articles.length ?? 0) > 1 &&
    gameState.phase == "Game";
  const scoreData = hasPlayedActiveRound ? gameState : gameState.last_round;
  const playerEntriesFound = Object.entries(scoreData.players).filter(
    ([_, data]) =>
      data.past_articles.length > 1 && data.found_targets.length > 0,
  );

  playerEntriesFound.sort((a, b) => {
    if (sortBy === "moves") {
      if (a[1].past_articles.length !== b[1].past_articles.length)
        return a[1].past_articles.length - b[1].past_articles.length;
      return (
        a[1].found_targets[0].found_time[0] -
        b[1].found_targets[0].found_time[0]
      );
    }
    if (
      a[1].found_targets[0].found_time[0] !==
      b[1].found_targets[0].found_time[0]
    )
      return (
        a[1].found_targets[0].found_time[0] -
        b[1].found_targets[0].found_time[0]
      );
    return a[1].past_articles.length - b[1].past_articles.length;
  });
  const playerEntriesNotFound = Object.entries(scoreData.players).filter(
    ([_, data]) =>
      data.past_articles.length > 1 && data.found_targets.length <= 0,
  );
  const standings = playerEntriesFound.concat(playerEntriesNotFound);

  const finishTime = (data: (typeof playerEntriesFound)[number][1]) =>
    data.found_targets[data.found_targets.length - 1]?.found_time[0] ??
    Infinity;

  const fastestPlayerId =
    playerEntriesFound.length > 0
      ? playerEntriesFound.reduce((a, b) => {
          const timeDiff = finishTime(a[1]) - finishTime(b[1]);
          if (timeDiff !== 0) return timeDiff < 0 ? a : b;
          return a[1].past_articles.length < b[1].past_articles.length ? a : b;
        })[0]
      : null;
  const fewestMovesPlayerId =
    playerEntriesFound.length > 0
      ? playerEntriesFound.reduce((a, b) => {
          const movesDiff =
            a[1].past_articles.length - b[1].past_articles.length;
          if (movesDiff !== 0) return movesDiff < 0 ? a : b;
          return finishTime(a[1]) < finishTime(b[1]) ? a : b;
        })[0]
      : null;

  const timelinePlayers = standings.map(([playerId, data]) => {
    const name = lobbyState?.players?.[playerId]?.name ?? playerId.slice(0, 8);
    const articles = data.past_articles.map((pa) => {
      const [secs, nanos] = pa.found_time;
      const dur = secs + nanos / 1000000000;
      return {
        text: pa.article.title,
        duration: dur,
        type: pa.is_target ? ("highlight" as const) : undefined,
      };
    });
    return { name, articles };
  });

  return (
    <div className={`h-dvh ${theme.bg} flex flex-col relative overflow-hidden`}>
      <MobileNav
        currentTab={mobileTabEndscreen}
        onTabChange={setMobileTabEndscreen}
        isDark={isDark}
        theme={theme}
        buttons={navbarButtonsEndscreen}
        className="flex-1 overflow-auto"
      >
        {/* Header */}
        <div
          className={`relative z-50 shrink-0 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <HomeLink />
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          {/* Winner Section */}
          {playerEntriesFound.length > 0 ? (
            <div className="text-center mb-10">
              <h1
                className={`text-3xl md:text-5xl font-bold tracking-tight ${theme.text} mb-2`}
              >
                {(scoreData.players[currentPlayerId]?.found_targets.length ??
                  0) > 0
                  ? t("openLobby.youFoundIt")
                  : t("openLobby.roundOver")}
              </h1>
            </div>
          ) : (
            <div className="text-center mb-10">
              <h1
                className={`text-3xl md:text-5xl font-bold tracking-tight ${theme.text} mb-2`}
              >
                {t("openLobby.roundOverHeading")}
              </h1>
            </div>
          )}

          {/* Open Lobby Preview Card */}
          <div className="mb-8">
            <OpenLobbyCard
              lobbyId={lobbyId}
              startTitle={gameState.start.title}
              targetTitle={gameState.target_article.title}
              phase={gameState.phase}
              gameRemaining={gameRemaining}
              scoreCountdown={scoreCountdown}
              hasPlayed={
                (gameState.players[currentPlayerId]?.found_targets.length ??
                  0) > 0
              }
              isDark={isDark}
              theme={theme}
              playerCount={Object.values(gameState.players).length}
            />
          </div>

          {/* Desktop Tab Navigation */}
          <div className="hidden md:flex justify-center mb-8">
            <TabGroup theme={theme}>
              {navbarButtonsEndscreen.map((button) => (
                <TabButton
                  key={button.id}
                  active={mobileTabEndscreen.id === button.id}
                  onClick={() => setMobileTabEndscreen(button)}
                  isDark={isDark}
                  theme={theme}
                >
                  {t(button.label)}
                </TabButton>
              ))}
            </TabGroup>
          </div>

          {/* Scoreboard */}
          <section
            className={
              mobileTabEndscreen === NavButtons.scoreboard ? "block" : "hidden"
            }
          >
            <div
              className={`${theme.sectionBg} ${theme.sectionBorder} rounded-2xl overflow-hidden`}
            >
              <div className="p-6 flex items-center justify-between gap-4">
                <SectionHeader
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  }
                  title={t("openLobby.finalStandings")}
                  theme={theme}
                  isDark={isDark}
                />
                <div
                  className={`inline-flex rounded-full border ${isDark ? "border-zinc-700" : "border-gray-200"} p-0.5 shrink-0`}
                >
                  <button
                    onClick={() => setSortBy("moves")}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      sortBy === "moves"
                        ? isDark
                          ? "bg-white text-black"
                          : "bg-gray-900 text-white"
                        : isDark
                          ? "text-zinc-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {t("openLobby.fewestMoves")}
                  </button>
                  <button
                    onClick={() => setSortBy("time")}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      sortBy === "time"
                        ? isDark
                          ? "bg-white text-black"
                          : "bg-gray-900 text-white"
                        : isDark
                          ? "text-zinc-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {t("openLobby.fastestTime")}
                  </button>
                </div>
              </div>

              <div
                className={`px-6 pb-3 flex items-center gap-2 ${theme.textMuted}`}
              >
                <span className="text-xs font-medium tracking-wide uppercase">
                  {t("openLobby.round")}
                </span>
                <span className={`text-xs font-medium ${theme.text}`}>
                  {scoreData.start.title}
                </span>
                <span className="text-xs opacity-50">→</span>
                <span className={`text-xs font-medium ${theme.text}`}>
                  {scoreData.target_article.title}
                </span>
              </div>
              <div className="px-6 pb-6 space-y-3">
                {(() => {
                  const formatTime = (seconds: number) => {
                    const m = Math.floor(seconds / 60);
                    const s = seconds % 60;
                    return m > 0 ? `${m}m ${s}s` : `${s}s`;
                  };

                  return standings.map((entry, index) => {
                    const playerName =
                      lobbyState?.players?.[entry[0]]?.name ??
                      entry[0].slice(0, 8);
                    const isFinder = index < playerEntriesFound.length;
                    const time = isFinder
                      ? entry[1].found_targets[0].found_time[0]
                      : null;
                    const showFastestIcon = entry[0] === fastestPlayerId;
                    const showFewestIcon = entry[0] === fewestMovesPlayerId;

                    return (
                      <div
                        key={entry[0]}
                        className={`p-4 rounded-xl flex justify-between items-center transition-all duration-300 ${
                          isDark
                            ? "bg-zinc-900/50 hover:bg-zinc-800/50"
                            : "bg-white hover:bg-gray-50"
                        }`}
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center gap-4">
                          <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isDark
                                ? "bg-zinc-800 text-zinc-400"
                                : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            <span className="text-sm font-bold">
                              {isFinder
                                ? index + 1
                                : gameState.phase === "Game" &&
                                    hasPlayedActiveRound
                                  ? t("openLobby.live")
                                  : t("openLobby.dnf")}
                            </span>
                          </span>
                          <div>
                            <ProfileAvatar
                              image={lobbyState?.players?.[entry[0]]?.image}
                              name={playerName}
                              size="sm"
                            />
                          </div>
                          <div>
                            <span
                              className={`font-medium ${entry[0] === currentPlayerId ? "font-semibold" : ""} ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {playerName}
                              {entry[0] === currentPlayerId && (
                                <span
                                  className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDark ? "bg-zinc-700 text-zinc-300" : "bg-gray-200 text-gray-600"}`}
                                >
                                  {t("common.you")}
                                </span>
                              )}
                              {showFastestIcon && (
                                <img
                                  src="/clock_without_crown.svg"
                                  className="h-5 ml-2 inline-block"
                                  alt=""
                                />
                              )}
                              {showFewestIcon && (
                                <img
                                  src="/mouse-click-icon.svg"
                                  className="h-5 ml-2 inline-block"
                                  alt=""
                                />
                              )}
                            </span>
                            <div className="mt-1">
                              <ProviderBadge
                                provider={
                                  lobbyState?.players?.[entry[0]]?.provider
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div
                            className={`text-md ${theme.textMuted} flex-1 text-right tabular-nums`}
                          >
                            {time !== null
                              ? formatTime(time)
                              : gameState.phase === "Game" &&
                                  hasPlayedActiveRound
                                ? "@ " +
                                  entry[1].past_articles[
                                    entry[1].past_articles.length - 1
                                  ]?.article.title
                                : t("openLobby.dnf")}
                          </div>
                          <div
                            className={`text-md ${theme.textMuted} w-20 text-right tabular-nums`}
                          >
                            {t("openLobby.moves", {
                              count: entry[1].past_articles.length,
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </section>

          {/* Player Paths */}
          <section
            className={
              mobileTabEndscreen === NavButtons.playerPaths ? "block" : "hidden"
            }
          >
            <div
              className={`${theme.sectionBg} ${theme.sectionBorder} rounded-2xl p-6`}
            >
              <SectionHeader
                icon={<SvgIcon markup={mapIcon} size={20} />}
                title={t("openLobby.playerPaths")}
                theme={theme}
                isDark={isDark}
              />

              {timelinePlayers.length > 0 &&
              timelinePlayers.some((p) => p.articles.length > 0) ? (
                <div className="mt-4">
                  <Timeline players={timelinePlayers} isDark={isDark} />
                </div>
              ) : (
                <EmptyState
                  isDark={isDark}
                  message={t("openLobby.noPathData")}
                  icon={
                    <SvgIcon markup={mapIcon} size={32} strokeWidth={1.5} />
                  }
                />
              )}
            </div>
          </section>

          {/* Back to Home */}
          <div className="mt-8 flex justify-center">
            <Button
              variant="primary"
              onClick={() => navigate("/")}
              isDark={isDark}
              className="w-full py-3"
            >
              {t("openLobby.backToHome")}
            </Button>
          </div>

          <div className="mt-8">
            <GeoBingoCard isDark={isDark} variant="openlobby" />
          </div>
        </div>
      </MobileNav>
    </div>
  );
}
